import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import MevzuatChatbot from '@/components/MevzuatChatbot';
import ThemeToggle from '@/components/ThemeToggle';
import RoleSwitcher, { getPreferredViewMode, getCurrentViewMode } from '@/components/RoleSwitcher';
import NotificationBell from '@/components/NotificationBell';
import {
  BookOpen, BarChart3, Users, Shield, Settings, LogOut, ChevronRight,
  GraduationCap, Video, ClipboardList, Award, Target, Building2,
  FileText, Clock, Home, Menu, X, ChevronLeft, Layers,
  AlertTriangle, Eye, Scale, Bell, RefreshCw, Inbox, ChevronDown
} from 'lucide-react';

// Her rolün "Ana Sayfa" olarak gideceği default rota.
// Login sonrası yönlendirme ve menüdeki Ana Sayfa linki bununla eşleşir.
function getHomePath(role, isInstructor) {
  if (role === 'super_admin') return '/super-admin';
  if (role === 'tenant_admin') return '/tenant-admin';
  if (role === 'auditor') return '/auditor';
  if (role === 'compliance_officer') return '/compliance';
  if (role === 'executive') return '/executive';
  if (role === 'manager') return '/manager';
  if (isInstructor) {
    // Dual-role users (compliance officer who also teaches) can flip
    // between instructor and learner mode via the sidebar toggle. The
    // chosen mode is remembered across reloads.
    return getPreferredViewMode() === 'learner' ? '/dashboard' : '/instructor';
  }
  return '/dashboard';
}

// Hangi roller öğrenci tarafı "Eğitim" menüsünü görür?
// Admin grubu (admin, tenant_admin, super_admin) görmez — onlar sadece yönetim yapar.
const LEARNER_MENU_ROLES = ['user', 'manager', 'compliance_officer', 'executive', 'auditor'];

function getMenuForUser(user, viewMode = 'learner') {
  if (!user) return [];
  const role = user.role || 'user';
  const isInstructor = !!user.is_instructor;
  const items = [];

  // Çift yetkili (instructor + öğrenci) kullanıcılarda sidebar'ın iki menü
  // setini birden göstermesini önlemek için viewMode ile filtreleniyor:
  //   learner    → sadece öğrenci menüleri + admin/ortak
  //   instructor → sadece öğretmen menüleri + admin/ortak
  // Admin/ortak menüler her iki modda görünür (mod bağımsız işler).
  const isInstructorMode = isInstructor && viewMode === 'instructor';
  // is_trainee ("eğitim alır") rolden bağımsız bir işarettir: kurum yöneticisi
  // hem yönetip hem kendi zorunlu eğitimini alabilir. İşaretli değilse admin
  // rollerinde Eğitim bölümü görünmez — menü sadeliği korunur.
  const isTrainee = user.is_trainee === true;
  const showLearnerMenu = (LEARNER_MENU_ROLES.includes(role) || isInstructor || isTrainee) && !isInstructorMode;
  const showInstructorMenu = isInstructorMode;

  // === ANA SAYFA (herkes için, role-based route) ===
  items.push({ path: getHomePath(role, isInstructor), icon: Home, label: 'Ana Sayfa' });

  // === EĞİTİM ===
  if (showLearnerMenu) {
    items.push({ type: 'header', label: 'Eğitim' });
    items.push({ path: '/egitimler', icon: BookOpen, label: 'Tüm Eğitimler' });
    items.push({ path: '/weak-topics', icon: Target, label: 'Zayıf Konularım' });
    items.push({ path: '/mevzuat', icon: Scale, label: 'Mevzuat' });
    items.push({ path: '/exams', icon: ClipboardList, label: 'Sınavlar' });
    items.push({ path: '/live-sessions', icon: Video, label: 'Canlı Dersler' });
    items.push({ path: '/certificate', icon: Award, label: 'Sertifikalar' });
    items.push({ path: '/content-updates', icon: Bell, label: 'İçerik Güncellemeleri' });
    items.push({ path: '/leaderboard', icon: BarChart3, label: 'Liderlik Tablosu' });
  }

  // === ÖĞRETMEN ===
  if (showInstructorMenu) {
    items.push({ type: 'header', label: 'Öğretmen' });
    items.push({ path: '/instructor', icon: BarChart3, label: 'Genel Bakış' });
    items.push({ path: '/instructor', icon: Users, label: 'Öğrenci İlerlemesi', hash: '#students' });
    // Canlı ders oluşturma/başlatma yetkisi zaten backend'de
    // (live_sessions.py — is_instructor kontrolü ile). Sidebar'da
    // görünür olması gerekiyordu: instructor modunda Eğitim section
    // gizli olduğu için önceden bu linke hiç ulaşılamıyordu.
    items.push({ path: '/live-sessions', icon: Video, label: 'Canlı Dersler' });
  }

  // === ROL BAZLI PANELLER ===
  // Tek sayfalık roller için (manager, compliance, executive) ayrı bölüm yok —
  // Ana Sayfa zaten kendi panellerine gidiyor. Sadece auditor'un 6 sekmesi var.
  if (role === 'auditor') {
    // AuditorPage 6 sekmeli. Hash-based link ile her sekme menüde ayrı item.
    items.push({ type: 'header', label: 'Denetçi' });
    items.push({ path: '/auditor', icon: Shield, label: 'Genel Bakış' });
    items.push({ path: '/auditor', icon: Users, label: 'Personel', hash: '#personnel' });
    items.push({ path: '/auditor', icon: BarChart3, label: 'Departman Raporu', hash: '#department-report' });
    items.push({ path: '/auditor', icon: ClipboardList, label: 'Eğitim Denetim İzleri', hash: '#audit-trail' });
    items.push({ path: '/auditor', icon: Target, label: 'Uyum Durumu', hash: '#compliance' });
    items.push({ path: '/auditor', icon: Video, label: 'Canlı Ders Katılım', hash: '#live-audit' });
    items.push({ path: '/auditor', icon: RefreshCw, label: 'Yenileme Yaklaşan', hash: '#renewals' });
    items.push({ path: '/tenant-admin/agent-training', icon: Users, label: 'Temsilci Eğitimi' });
    // Denetim kapsamında auditor'un iç mevzuat ve duyuruları incelemesi
    // gerekiyor; `requireAudit` backend tarafı zaten izin veriyor ama sidebar
    // linki yoktu — URL yazmak zorunda kalıyordu.
    items.push({ path: '/tenant-admin/bulletins', icon: Bell, label: 'Uyum Duyuruları' });
    items.push({ path: '/tenant-admin/ic-dokumanlar', icon: FileText, label: 'İç Mevzuat' });
    items.push({ path: '/regulator-report', icon: FileText, label: 'Regülatör Rapor' });
  }

  // Compliance officer (uyum görevlisi) — temsilci eğitim raporlarını görür
  if (role === 'compliance_officer') {
    items.push({ type: 'header', label: 'Uyum Görevlisi' });
    items.push({ path: '/compliance', icon: Target, label: 'Uyum Panosu' });
    // Denetçi panosunu salt-okunur görür (require_auditor_access izni ile,
    // rapor üretme bloke).
    items.push({ path: '/auditor', icon: Shield, label: 'Denetçi Panosu' });
    items.push({ path: '/tenant-admin/bulletins', icon: Bell, label: 'Uyum Duyuruları' });
    items.push({ path: '/tenant-admin/ic-dokumanlar', icon: FileText, label: 'İç Mevzuat' });
    items.push({ path: '/tenant-admin/agent-training', icon: Users, label: 'Temsilci Eğitim Kayıtları' });
  }

  // Yönetici (manager) — tek sayfalık panonun içindeki bölümlere kısayol
  // (hash anchor). Kullanıcı "ekibimi nereden yönetirim?" sorusuna sidebar'dan
  // kılavuzlanır.
  if (role === 'manager') {
    items.push({ type: 'header', label: 'Yönetici' });
    items.push({ path: '/manager', icon: Users, label: 'Ekip Üyeleri', hash: '#ekip' });
    items.push({ path: '/manager', icon: BarChart3, label: 'Departman Karşılaştırma', hash: '#departman-karsilastirma' });
  }

  // Üst Yönetim (executive) — stratejik paneldeki 3 ana bloka hash anchor.
  if (role === 'executive') {
    items.push({ type: 'header', label: 'Üst Yönetim' });
    items.push({ path: '/executive', icon: Shield, label: 'Kurumsal Skorkart', hash: '#skorkart' });
    items.push({ path: '/executive', icon: Scale, label: 'Regülatör Uyum', hash: '#regulasyon' });
    items.push({ path: '/executive', icon: BarChart3, label: 'Departman Skorları', hash: '#departman-skorlari' });
  }

  // Dış regülatör: salt-okunur; sadece raporlama sekmeleri.
  if (role === 'external_regulator') {
    items.push({ type: 'header', label: 'Dış Regülatör' });
    items.push({ path: '/regulator-report', icon: FileText, label: 'Regülatör Rapor' });
    items.push({ path: '/auditor', icon: Shield, label: 'Denetçi Panosu' });
    items.push({ path: '/auditor', icon: ClipboardList, label: 'Eğitim Denetim İzleri', hash: '#audit-trail' });
    items.push({ path: '/auditor', icon: Target, label: 'Uyum Durumu', hash: '#compliance' });
    items.push({ path: '/auditor', icon: RefreshCw, label: 'Yenileme Yaklaşan', hash: '#renewals' });
  }

  // === ŞİRKET YÖNETİMİ ===
  // Süper admin bu linkleri görmez — tenant_id'si olmadığı için tenant-scoped
  // sayfalar boş açılıyordu (kafa karışıklığı). Süper admin tenant işleri için
  // /super-admin/tenants → tenant seç → detay sayfası akışını kullanır.
  if (role === 'tenant_admin') {
    items.push({ type: 'header', label: 'Şirket' });
    items.push({ path: '/tenant-admin', icon: Building2, label: 'Şirket Paneli' });
    items.push({ path: '/tenant-admin/department-assignments', icon: Layers, label: 'Eğitim Atamaları' });
    items.push({ path: '/tenant-admin/curriculum', icon: BookOpen, label: 'Müfredat' });
    items.push({ path: '/tenant-admin/instructors', icon: GraduationCap, label: 'Öğretmenler' });
    items.push({ path: '/tenant-admin/users', icon: Users, label: 'Kullanıcı Yönetimi' });
    items.push({ path: '/tenant-admin/bulletins', icon: Bell, label: 'Uyum Duyuruları' });
    items.push({ path: '/tenant-admin/ic-dokumanlar', icon: FileText, label: 'İç Mevzuat' });
    items.push({ path: '/tenant-admin/agent-training', icon: Users, label: 'Temsilci Eğitimi' });
    items.push({ path: '/tenant-admin/webhooks', icon: Layers, label: 'Webhook' });
    items.push({ path: '/tenant-admin/settings', icon: Settings, label: 'Şirket Ayarları' });
  }

  // === YÖNETİM (admin işlemleri) ===
  if (['super_admin', 'tenant_admin'].includes(role)) {
    items.push({ type: 'header', label: 'Yönetim' });
    items.push({ path: '/admin', icon: BarChart3, label: 'Dashboard' });
    items.push({ path: '/admin/reports', icon: BarChart3, label: 'Raporlar' });
    items.push({ path: '/admin/users', icon: Users, label: 'Personeller' });
    items.push({ path: '/admin/content', icon: FileText, label: 'İçerik' });
    items.push({ path: '/admin/audit-logs', icon: ClipboardList, label: 'Sistem Denetim İzleri' });
    items.push({ path: '/admin/notifications', icon: AlertTriangle, label: 'Sistem Bildirimleri' });
    items.push({ path: '/admin/bbb', icon: Video, label: 'Canlı Ders (BBB)' });
    items.push({ path: '/admin/training-periods', icon: Clock, label: 'Periyotlar' });
  }

  // === AI & İÇERİK (admin) ===
  if (['super_admin', 'tenant_admin'].includes(role)) {
    items.push({ type: 'header', label: 'AI & İçerik' });
    items.push({ path: '/admin/documents', icon: Layers, label: 'Doküman Yönetimi' });
    items.push({ path: '/admin/content-gen', icon: Eye, label: 'İçerik Üretici' });
  }

  // === SUPER ADMIN ===
  if (role === 'super_admin') {
    items.push({ type: 'header', label: 'Platform' });
    items.push({ path: '/super-admin', icon: Shield, label: 'Platform Yönetimi' });
    items.push({ path: '/super-admin/tenants', icon: Building2, label: 'Tenantlar' });
    items.push({ path: '/super-admin/demo-requests', icon: Inbox, label: 'Demo Talepleri' });
    items.push({ path: '/super-admin/cms', icon: FileText, label: 'CMS' });
    items.push({ path: '/super-admin/sectors', icon: Layers, label: 'Sektörler' });
    items.push({ path: '/super-admin/packages', icon: Award, label: 'Paketler' });
  }

  // Dedupe by path+hash, then drop headers whose section became empty
  const seen = new Set();
  const deduped = items.filter(i => {
    if (i.type === 'header') return true;
    const key = i.path + (i.hash || '');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return deduped.filter((i, idx) => {
    if (i.type !== 'header') return true;
    // Keep header only if at least one non-header item follows before the next header
    for (let j = idx + 1; j < deduped.length; j++) {
      if (deduped[j].type === 'header') return false;
      return true;
    }
    return false;
  });
}

function Breadcrumb({ location }) {
  const segments = location.pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return null;
  const labels = {
    'dashboard': 'Dashboard', 'tenant-admin': 'Şirket Yönetimi',
    'super-admin': 'Platform', 'auditor': 'Denetim', 'instructor': 'Öğretmen',
    'live-sessions': 'Canlı Dersler', 'exams': 'Sınavlar', 'certificates': 'Sertifikalar',
    'manager': 'Yönetici', 'compliance': 'Uyum', 'executive': 'Üst Yönetim',
    'users': 'Personeller', 'settings': 'Ayarlar', 'content': 'İçerik',
    'bbb': 'BBB', 'training-periods': 'Periyot', 'instructors': 'Öğretmenler',
    'curriculum': 'Müfredat', 'department-assignments': 'Atamalar',
    'regulator-report': 'Regülatör Rapor', 'audit-logs': 'Denetim İzleri',
    'reports': 'Raporlar', 'weak-topics': 'Zayıf Konularım',
    'content-updates': 'İçerik Güncellemeleri',
  };
  return (
    <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4 px-1" data-testid="breadcrumb">
      <Link to="/dashboard" className="hover:text-foreground transition-colors">Ana Sayfa</Link>
      {segments.map((seg, i) => (
        <React.Fragment key={i}>
          <ChevronRight className="w-3 h-3" />
          {i === segments.length - 1 ? (
            <span className="text-foreground font-medium">{labels[seg] || seg}</span>
          ) : (
            <Link to={`/${segments.slice(0, i + 1).join('/')}`} className="hover:text-foreground transition-colors">
              {labels[seg] || seg}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navigating, setNavigating] = useState(false);
  // Mobile sidebar'da kullanıcı bir section'ı daralttıysa burada tutulur.
  // Yüksek item'lı (>=5) section'lar mobile'da varsayılan olarak daraltık.
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const prevPath = useRef(location.pathname);

  // Route change detection → show progress bar
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      setNavigating(true);
      prevPath.current = location.pathname;
      const timer = setTimeout(() => setNavigating(false), 800);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Hash-based anchor scroll. AuditorPage'de hash → tab state, ama Manager/
  // Executive gibi tek-sayfa panolarda hash bir DOM id'sine scroll etmeli.
  // React Router tek başına bunu yapmıyor; bu effect DOM hazır olunca
  // elemana scrollIntoView çağırıyor.
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [location.hash, location.pathname]);

  const viewMode = user?.is_instructor ? getCurrentViewMode(location.pathname) : 'learner';
  const menuItems = getMenuForUser(user, viewMode);
  const isActive = (path) => location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path + '/'));

  // Pages that should NOT have sidebar (login, signup, embed, etc)
  const noLayoutPaths = ['/login', '/signup', '/accept-invite', '/verify-certificate', '/keycloak-callback', '/kurumsal', '/bireysel', '/tanitim', '/demo', '/denetim-kanit', '/denetim-ozet', '/sozlesme', '/kvkk-aydinlatma-metni', '/cerez-politikasi', '/kullanim-sartlari', '/invite/', '/embed/', '/rapor-goruntule/'];
  if (noLayoutPaths.some(p => location.pathname.startsWith(p))) {
    return <>{children}</>;
  }
  // Ana sayfa (/) — LandingPage kendi nav'ıyla gelir, sidebar gösterme.
  // Oturum açık ya da kapalı fark etmez; logo → / her zaman tanıtımı açar.
  if (location.pathname === '/') {
    return <>{children}</>;
  }

  // Live room - full screen, no layout
  if (location.pathname.match(/^\/live\/[^/]+$/) && document.querySelector('[data-testid="live-room"]')) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-layout">
      {/* Global print CSS — PDF export için sidebar, header ve navigasyon
          gizlenir; sadece `.print-area` içeriği sayfaya basılır. */}
      <style>{`
        @media print {
          aside[data-testid="sidebar"],
          header[data-testid="global-header-mobile"],
          .no-print { display: none !important; }
          main, .print-area { width: 100% !important; max-width: 100% !important; }
        }
      `}</style>
      {/* SIDEBAR - Desktop */}
      <aside className={`hidden lg:flex flex-col border-r border-border transition-all duration-300 h-screen sticky top-0 overflow-hidden ${sidebarOpen ? 'w-[260px]' : 'w-16'}`} style={{backgroundColor: 'hsl(var(--sidebar-bg))'}} data-testid="sidebar">
        {/* Logo */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border shrink-0">
          {sidebarOpen ? (
            <Link to="/" className="flex items-center gap-2" title="Ana sayfaya dön">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-bold text-sm">UyumAkademi</span>
            </Link>
          ) : (
            <Link to="/" title="Ana sayfaya dön">
              <Shield className="w-6 h-6 text-primary mx-auto" />
            </Link>
          )}
          {sidebarOpen && <ThemeToggle />}
        </div>

        {/* Role switcher — only for users who are also instructors */}
        {user?.is_instructor && <RoleSwitcher collapsed={!sidebarOpen} />}

        {/* Nav Items */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {menuItems.map((item, idx) => {
            if (item.type === 'header') {
              return sidebarOpen ? (
                <div key={`h-${idx}`} className="pt-4 pb-1 px-3 first:pt-0">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{item.label}</span>
                </div>
              ) : (
                <div key={`h-${idx}`} className="pt-3 pb-1"><div className="border-t border-border mx-2" /></div>
              );
            }
            const Icon = item.icon;
            const fullPath = item.path + (item.hash || '');
            const active = isActive(item.path) && (!item.hash || location.hash === item.hash);
            return (
              <Link key={fullPath} to={fullPath}
                onClick={() => setNavigating(true)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                  active ? 'border-l-[3px] border-l-primary text-primary font-semibold bg-primary/5' : 'border-l-[3px] border-l-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                } ${!sidebarOpen ? 'justify-center px-0' : ''}`}
                title={!sidebarOpen ? item.label : undefined}>
                <Icon className="w-4 h-4 shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-border p-2 space-y-1">
          <div className={`flex items-center py-1 px-3 ${!sidebarOpen ? 'justify-center px-0' : 'justify-between'}`}>
            {sidebarOpen && <span className="text-xs text-muted-foreground">Bildirim</span>}
            <NotificationBell collapsed={!sidebarOpen} />
          </div>
          <div className={`flex items-center py-1 px-3 ${!sidebarOpen ? 'justify-center px-0' : 'justify-between'}`}>
            {sidebarOpen && <span className="text-xs text-muted-foreground">Tema</span>}
            <ThemeToggle />
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`w-full flex items-center gap-2 py-2 px-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-xs ${!sidebarOpen ? 'justify-center px-0' : ''}`}>
            <ChevronLeft className={`w-4 h-4 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
            {sidebarOpen && <span>Daralt</span>}
          </button>
          <button onClick={() => navigate('/profile')}
            className={`w-full flex items-center gap-2 py-2 px-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[#F1F5F9] transition-colors text-xs ${!sidebarOpen ? 'justify-center px-0' : ''}`}>
            <Users className="w-4 h-4" />
            {sidebarOpen && <span>Profilim</span>}
          </button>
          <button onClick={() => { logout(); navigate('/login'); }}
            className={`w-full flex items-center gap-2 py-2 px-3 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors text-xs ${!sidebarOpen ? 'justify-center px-0' : ''}`}>
            <LogOut className="w-4 h-4" />
            {sidebarOpen && <span>Çıkış Yap</span>}
          </button>
        </div>
      </aside>

      {/* MOBILE SIDEBAR OVERLAY */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-card border-r border-border flex flex-col z-10">
            <div className="h-14 flex items-center justify-between px-4 border-b border-border">
              <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2" title="Ana sayfaya dön">
                <Shield className="w-6 h-6 text-primary" />
                <span className="font-bold text-sm">UyumAkademi</span>
              </Link>
              <button onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            {user?.is_instructor && <RoleSwitcher />}
            <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
              {(() => {
                // Mobil: çok item'lı section'ları varsayılan kapalı render
                // et; kullanıcı header'a tıklayınca açılır. Aktif rotayı
                // içeren section her zaman açık kalır.
                const groups = [];
                let current = null;
                menuItems.forEach((item) => {
                  if (item.type === 'header') {
                    current = { header: item, items: [] };
                    groups.push(current);
                  } else if (current) {
                    current.items.push(item);
                  } else {
                    // Header olmadan başta gelen item (ör: Ana Sayfa) —
                    // kendi tek item'lık grup.
                    groups.push({ header: null, items: [item] });
                  }
                });

                return groups.map((g, gi) => {
                  const hasActive = g.items.some(it => isActive(it.path));
                  const manuallyCollapsed = g.header && collapsedSections.has(g.header.label);
                  const manuallyExpanded = g.header && !manuallyCollapsed && collapsedSections.has('__expanded__' + g.header.label);
                  // Section açık mı? Rol: aktif rota içeren açık. 5+
                  // item'lı section varsayılan kapalı (kullanıcı açabilir).
                  const defaultCollapsed = g.header && g.items.length >= 5;
                  const open = hasActive || manuallyExpanded || (!defaultCollapsed && !manuallyCollapsed);
                  return (
                    <div key={`mg-${gi}`}>
                      {g.header && (
                        <button
                          type="button"
                          onClick={() => {
                            setCollapsedSections(prev => {
                              const next = new Set(prev);
                              // Aktif section'ı zorla kapatmayı engelle — anlamsız
                              if (hasActive) return next;
                              if (open) {
                                next.add(g.header.label);
                                next.delete('__expanded__' + g.header.label);
                              } else {
                                next.delete(g.header.label);
                                next.add('__expanded__' + g.header.label);
                              }
                              return next;
                            });
                          }}
                          className="w-full pt-4 pb-1 px-3 first:pt-0 flex items-center justify-between group"
                        >
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 group-hover:text-foreground">{g.header.label}</span>
                          <ChevronDown className={`w-3 h-3 text-muted-foreground/60 transition-transform ${open ? '' : '-rotate-90'}`} />
                        </button>
                      )}
                      {open && g.items.map((item) => {
                        const Icon = item.icon;
                        const fullPath = item.path + (item.hash || '');
                        return (
                          <Link key={fullPath} to={fullPath} onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive(item.path) ? 'border-l-[3px] border-l-primary text-primary font-semibold bg-primary/5' : 'border-l-[3px] border-l-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}>
                            <Icon className="w-4 h-4" /><span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  );
                });
              })()}
            </nav>
            {/* Mobile Footer - Tema/Profil/Çıkış (UI-01 fix) */}
            <div className="border-t border-border p-2 space-y-1 shrink-0">
              <div className="flex items-center py-1 px-3 justify-between">
                <span className="text-xs text-muted-foreground">Tema</span>
                <ThemeToggle />
              </div>
              <button onClick={() => { navigate('/profile'); setMobileOpen(false); }}
                className="w-full flex items-center gap-2 py-2 px-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-xs">
                <Users className="w-4 h-4" /><span>Profilim</span>
              </button>
              <button onClick={() => { logout(); navigate('/login'); setMobileOpen(false); }}
                className="w-full flex items-center gap-2 py-2 px-3 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors text-xs">
                <LogOut className="w-4 h-4" /><span>Çıkış Yap</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* HEADER - Minimal global bar */}
        <header className="sticky top-0 z-40 h-12 flex items-center justify-between px-4 border-b border-border bg-background/80 backdrop-blur-xl lg:hidden" data-testid="global-header-mobile">
          <button onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <NotificationBell collapsed />
            <span className="text-xs text-muted-foreground">{user?.name}</span>
            <ThemeToggle />
          </div>
        </header>

        {/* PAGE CONTENT */}
        <main className="flex-1 overflow-y-auto relative">
          {/* SSO ile gelmiş ama Keycloak grubu hiçbir tenant mapping ile
              eşleşmemiş kullanıcı için uyarı — yine de tüm eğitimleri görür. */}
          {user?.department === 'unassigned' && (
            <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 px-4 py-2.5 text-sm">
              <strong>Departman ataması bekleniyor.</strong>{' '}
              Hesabınız henüz bir birime atanmadı, tüm eğitimleri inceleyebilirsiniz.
              Yöneticinizle iletişime geçerek atamanın yapılmasını isteyin.
            </div>
          )}
          {/* Navigation Progress Bar */}
          {navigating && (
            <div className="absolute top-0 left-0 right-0 z-50 h-1 bg-primary/20 overflow-hidden">
              <div className="h-full bg-primary animate-progress-bar" style={{
                animation: 'progress 0.8s ease-in-out',
              }} />
              <style>{`
                @keyframes progress {
                  0% { width: 0%; }
                  50% { width: 70%; }
                  100% { width: 100%; }
                }
                .animate-progress-bar { animation: progress 0.8s ease-in-out forwards; }
              `}</style>
            </div>
          )}
          {children}
        </main>

        {/* FOOTER */}
        <footer className="border-t border-border py-3 px-4" data-testid="global-footer">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>2026 UyumAkademi — Finansal Uyum Eğitim Platformu</span>
            <span className="flex items-center gap-3">
              <span>Powered by <a href="https://mobilarge.com.tr" target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline">Mobilarge</a></span>
              <span className="text-muted-foreground/50">v3.23</span>
            </span>
          </div>
        </footer>
      </div>

      {/* Mevzuat Chatbot - Her sayfada */}
      <MevzuatChatbot />
    </div>
  );
}
